/**
 * The courses each program teaches, in one place.
 *
 * Reports, the course list and the question bank each used to build their own
 * idea of "our courses": the bank filed under eight of them while Reports and
 * Course Offerings listed twenty-five. They now all read this: a course exists
 * here once, and the bank has a folder for every course of the active program.
 */

import { MOCK_COURSES, type CourseOffering } from "@/lib/mock/exam-data"

export type ProgramKey = "nursing" | "pt"

/** The school's program id (`son`, `spt`) to the catalog it teaches. Nursing is the default. */
export function programKeyOf(programId: string | undefined | null): ProgramKey {
  return programId === "spt" ? "pt" : "nursing"
}

const PT_TERM = {
  academicYear: "2025-2026",
  term: "Summer 2026",
  startDate: "2026-06-01",
  endDate: "2026-08-31",
  status: "Active" as const,
  assessmentCounts: { draft: 0, scheduled: 0, live: 0, evaluating: 0, resultsPublished: 0 },
}

const pt = (
  n: number,
  code: string,
  title: string,
  cohort: string,
  credits: number,
  instructor: string,
  enrollmentCount: number,
  courseType: CourseOffering["courseType"] = "Lecture",
): CourseOffering => ({ id: `pt${n}`, code, title, cohort, credits, instructor, enrollmentCount, courseType, ...PT_TERM })

/** Doctor of Physical Therapy. Codes match the PT course folders the bank has always used. */
export const PT_COURSES: CourseOffering[] = [
  pt(1, "PT-501", "Foundational Sciences", "DPT Class of 2028", 4, "Dr. Marcus Hale", 42),
  pt(2, "PT-520", "Musculoskeletal Systems", "DPT Class of 2028", 5, "Dr. Elena Voss", 42),
  pt(3, "PT-530", "Cardiopulmonary", "DPT Class of 2027", 4, "Dr. Tomas Ibarra", 40),
  pt(4, "PT-540", "Professional Ethics", "DPT Class of 2028", 2, "Dr. Grace Whitfield", 42, "Seminar"),
  pt(5, "PT-550", "Pediatrics", "DPT Class of 2027", 3, "Dr. Naomi Reyes", 40),
  pt(6, "PT-560", "Geriatrics", "DPT Class of 2027", 3, "Dr. Samuel Oduya", 40),
  pt(7, "PT-580", "Research Methods", "DPT Class of 2026", 3, "Dr. Lena Karlsson", 38, "Seminar"),
  pt(8, "PT-590", "Capstone", "DPT Class of 2026", 4, "Dr. Priya Menon", 38, "Project"),
  pt(9, "PT-600", "Board Review", "DPT Class of 2026", 2, "Dr. Daniel Brooks", 38, "Seminar"),
  pt(10, "PT-610", "Clinical Integration", "DPT Class of 2026", 6, "Dr. Hannah Cole", 38, "Lab"),
]

/** Every course in the workspace, for lookups by id. */
export const ALL_COURSES: CourseOffering[] = [...MOCK_COURSES, ...PT_COURSES]

const byCode = (a: CourseOffering, b: CourseOffering) => a.code.localeCompare(b.code)
const NURSING_BY_CODE = [...MOCK_COURSES].sort(byCode)
const PT_BY_CODE = [...PT_COURSES].sort(byCode)

/** A program's courses, in code order: the order the bank's folders and Reports both list them. */
export function coursesForProgram(program: ProgramKey): CourseOffering[] {
  return program === "pt" ? PT_BY_CODE : NURSING_BY_CODE
}

/** `NURS-120 Fundamentals of Nursing`: the name the bank folder and Reports both print. */
export function courseLabel(course: Pick<CourseOffering, "code" | "title">): string {
  return `${course.code} ${course.title}`
}

/** The bank folder id for a course. */
export function courseFolderId(course: Pick<CourseOffering, "code">): string {
  return `course-${course.code.toLowerCase()}`
}

/** Which program a bank folder belongs to, from the course folder it sits under. */
export function programOfCourseFolderId(folderId: string): ProgramKey {
  return folderId.startsWith("course-pt-") ? "pt" : "nursing"
}
